import {
  Table as ShadTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
} from "@/components/ui/table";
import React, { useState, useEffect, cloneElement } from "react";
import Button from "./Button";
import Input from "./Input";
import Icons from "@/assets/icons";
import { useTranslation } from "react-i18next";
import { messageService } from "@/services/MessageService";
import { ApiService } from "@/services/ApiService";
import { useNavigate } from "react-router-dom";
import TableAction from "./TableAction";
import LogDialog from "./LogDialog";
import { useAuth } from "@/contexts/Auth";
import UploadFileDialog from "./UploadFileDialog";
import FrequenciaDialog from "../pages/auth/licenca/FrequenciaDialog";
import ExpiracaoDialog from "../pages/auth/licenca/ExpiracaoDialog";
// @ts-ignore
import { toast } from "sonner";
import { downloadFileFromResponse } from "@/lib/utils";

interface Table {
  columns: any[];
  defaultData: any[];
  search?: boolean;
  buttonLabel?: string;
  onButtonClick?: () => void;
  isButtonDisabled?: boolean;
  baseRoute?: string;
  entity: string;
  allowedActions?: string[];
  onRefresh?: () => void;
  onEditAction?: (item: any) => void;
}

type logsRequest = {
  method: string;
  url: string;
  body?: any;
};

const formatPermissionName = (permission: string): string => {
  const parts = permission.replace("ROLE_", "").split("_");
  if (parts.length < 2) return permission;

  const [entity, action] = parts;
  const reversedParts = [action, entity];

  return reversedParts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const Table: React.FC<Table> = ({
  columns,
  defaultData,
  search,
  buttonLabel,
  onButtonClick,
  isButtonDisabled = false,
  baseRoute,
  entity,
  allowedActions,
  onRefresh,
  onEditAction,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [data, setData] = useState<any>([]);
  const [filteredData, setFilteredData] = useState<any>([]);
  const [logsRequest, setLogsRequest] = useState<logsRequest | null>(null);
  const [isPolling, setIsPolling] = useState(true);
  const [openLog, setOpenLog] = useState(false);
  const { usuario } = useAuth();
  const [openUpload, setOpenUpload] = useState(false);
  const [currentUploadId, setCurrentUploadId] = useState<string | null>(null);
  const [openFrequencia, setOpenFrequencia] = useState(false);
  const [currentFrequenciaId, setCurrentFrequenciaId] = useState<string | null>(null);
  const [currentFrequenciaSegundos, setCurrentFrequenciaSegundos] = useState<number | null>(null);
  const [openExpiracao, setOpenExpiracao] = useState(false);
  const [currentExpiracaoId, setCurrentExpiracaoId] = useState<string | null>(null);
  const [currentExpiracaoData, setCurrentExpiracaoData] = useState<any>(null);

  const navigate = useNavigate();
  const listRoute = entity === "transacao" ? "listarTabela" : "listar";
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const updateTable = async () => {
    if (onRefresh) {
      onRefresh();
      return;
    }

    const { data } = await ApiService.get(`${baseRoute}/${listRoute}`);
    setData(data);
    setFilteredData(data);
  };

  const getNestedObject = (obj: any, path: string) => {
    return path.split(".").reduce((acc, part: string) => acc?.[part], obj);
  };

  const Actions = ({ id, habilitada, values }: any) => {
    
    const isDownloadDisabled = entity === 'licenca' && values?.status != 'ATIVA';
    
    const actionsArray = [
      <TableAction
        id="executar"
        icon={<Icons.Play pathStyle="fill-sucesso-700" />}
        title="Executar"
        onClick={() => {
          setOpenLog(true);
          setLogsRequest({
            method: "POST",
            url: "api/v1/transacao/execucaoAvulsa",
            body: { transacaoId: id },
          });
          setIsPolling(false);
        }}
      />,
      <TableAction
        id="zerar"
        icon={<Icons.Cached pathStyle="fill-primario-700" />}
        title="Zerar"
        onClick={async () => {
          await ApiService.get(`${baseRoute}/zerar/${id}`);
          updateTable();
        }}
      />,
      <TableAction
        id="duplicar"
        icon={<Icons.ContentCopy pathStyle="fill-primario-700" />}
        title="Duplicar"
        onClick={async () => {
          try {
            await ApiService.get(`${baseRoute}/duplicar/${id}`);
            updateTable();
          } catch (e: any) {
            if (e?.status === 403) throw e;
            messageService.warn(
              t("table.messages.warn.duplicate"),
              e.data?.detail ?? e
            );
          }
        }}
      />,
      <TableAction
        id="upload"
        icon={<Icons.UploadFile pathStyle="fill-primario-700" />}
        title="Upload"
        onClick={() => {
          setCurrentUploadId(id);
          setOpenUpload(true);
        }}
      />,
      <TableAction
        id="editar"
        icon={<Icons.Edit pathStyle="fill-alerta-500" />}
        title="Editar"
        onClick={() => {
            if (onEditAction) {
                onEditAction(values);
            } else {
                navigate(`editar-${entity}/${id}`);
            }
        }}
      />,

      <TableAction
        id="remover"
        // Mantém visual de desabilitado se já estiver inativo (opcional) ou erro se for delete
        icon={<Icons.Delete pathStyle={"fill-erro-500"} />}
        title="Remover"
        onClick={() => {
          messageService.duasOpcoes(
            t("table.messages.twoOptions.remove.title"),
            t("table.messages.twoOptions.remove.description"),
            async () => {
              try {
                await ApiService.delete(`${baseRoute}/deletar/${id}`);
                updateTable();
              } catch (error: any) {
                let errorMsg = t("apiService.messages.badRequest");

                if (typeof error === "string") {
                  errorMsg = error;
                } else {
                  errorMsg = error?.response?.data?.message 
                          || error?.response?.data?.detail 
                          || error?.message 
                          || errorMsg;
                }

              const entityName = t(`table.messages.entities.${entity}`, { 
                  defaultValue: entity.charAt(0).toUpperCase() + entity.slice(1) 
              });
              const errorTitle = `${t("table.messages.erroDelete")} ${entityName}`; 

              messageService.errorWithDetail(
                errorTitle, 
                errorMsg
              );
              }
            }
          );
        }}
      />,

      <TableAction
        id="permissao"
        icon={<Icons.EyeOpen pathStyle="fill-primario-700" />}
        title="Mostrar permissões"
        onClick={() =>
          messageService.infoWithComponent(
            t("table.messages.info.permissions.title"),
            <ul
              className={`grid ${
                values.permissoes?.length > 1 && "sm:grid-cols-[auto_auto]"
              }`}
            >
              {values.permissoes?.map((string: string) => (
                <li
                  className="flex gap-2 whitespace-nowrap p-2 border-b"
                  key={string}
                >
                  <Icons.Check />{" "}
                  {t("table.messages.info.permissions.prefix")}{" "}
                  <strong>{formatPermissionName(string)}</strong>
                </li>
              ))}
            </ul>
          )
        }
      />,
     <TableAction
        id="download"
        icon={<Icons.Download pathStyle={isDownloadDisabled ? "fill-neutro-300" : "fill-neutro-500"} />}
        title={isDownloadDisabled ? "Aguardando ativação (Sem agente gerado)" : "Download do agente"}
        style={isDownloadDisabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
        onClick={async () => {
          if (isDownloadDisabled) return;

          try {
            const response = await ApiService.getFile(`${baseRoute}/${id}/downloadAgent`);
            downloadFileFromResponse(response, "agente-install.zip");
            toast.success(t("fileActions.downloadSuccessAgent.title"));
          } catch (error) {
            console.error("Erro ao baixar agente:", error);
          }
        }}
      />,
      <TableAction
        id="habilitar"
        icon={
          habilitada === "SIM" ? (
            <Icons.Enabled
              circleStyle="fill-sucesso-700"
              rectStyle="stroke-sucesso-700"
            />
          ) : (
            <Icons.Disabled
              circleStyle="fill-neutro-700"
              rectStyle="stroke-neutro-700"
            />
          )
        }
        title={habilitada === "SIM" ? "Desabilitar" : "Habilitar"}
        onClick={async () => {
          await ApiService.get(`${baseRoute}/habilitar/${id}`);
          updateTable();
        }}
      />,
      
      <TableAction
        id="editar_frequencia"
        icon={<Icons.Cached pathStyle="fill-primario-700" />}
        title={t("table.licenca.actionFrequencia.enabled", "Editar frequência")}
        onClick={() => {
          setCurrentFrequenciaId(id);
          setCurrentFrequenciaSegundos(
            values?.horaAgente ? Number(values.horaAgente) : null
          );
          setOpenFrequencia(true);
        }}
      />,
      
      <TableAction
        id="log"
        icon={<Icons.RemoveRedEye pathStyle="fill-primario-700" />}
        title="Visualizar logs"
        onClick={() => {
          setOpenLog(true);
          setLogsRequest({
            method: "GET",
            url: `api/v1/transacao/visualizarLogs/${id}`,
          });
          setIsPolling(true);
        }}
      />,
    ];

    return allowedActions ? (
      <div className="flex items-center justify-center gap-3">
        {actionsArray
          // 1. Filtra apenas pelos acessos globais do usuário (RBAC)
          .filter((action) => allowedActions.includes(action.props.id))
          .map((action) => {
            
            // 2. Regras de visibilidade específicas da linha
            const isFrequenciaHidden = action.props.id === "editar_frequencia" && values?.tipoLicenca !== "SAAS";

            return (
              <div 
                key={action.props.id} 
                // Se estiver oculto, usa 'invisible' (transparente mas ocupa espaço) para não quebrar o layout
                className={isFrequenciaHidden ? "invisible pointer-events-none" : "flex items-center justify-center"}
              >
                {cloneElement(action)}
              </div>
            );
          })}
      </div>
    ) : null;
  };

  useEffect(() => {
    setData(defaultData);
    setFilteredData(defaultData);
  }, [defaultData]);

  const handleSearchChange = ({ target }: any) => {
    setSearchValue(target.value);
  };

  const handleColumnFilterChange = (accessor: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [accessor]: value,
    }));
    setPage(1);
  };

  const handleTableItens = (accessor: string, values: any) => {
    
    // NOVO: Tratamento do Status (Enum)
    if (accessor === "status") {
      return values[accessor] === "ATIVO" ? (
        <span className="text-sucesso-700 font-bold">{t("table.user.body.active.true", "Ativo")}</span>
      ) : (
        <span className="text-neutro-500">{t("table.user.body.active.false", "Inativo")}</span>
      );
    }

    // Mantido para compatibilidade, caso algum outro lugar ainda use boolean
    if (accessor === "ativo") {
      return values[accessor] ? (
        <span className="text-sucesso-700 font-bold">{t("table.user.body.active.true")}</span>
      ) : (
        <span className="text-neutro-500">{t("table.user.body.active.false")}</span>
      );
    }

    if (accessor === "statusFormatado" && entity === "licenca") {
      const rawStatus = values.status;
      const isGerando = rawStatus === "GERANDO_AGENTE_CLI";
      const statusColor = 
        rawStatus === 'ATIVA' ? 'text-sucesso-700' :
        rawStatus === 'FALHA_GERACAO' || rawStatus === 'VENCIDA' ? 'text-erro-500' :
        'text-primario-700';

      return (
        <div className="flex items-center gap-2">
          <span className={`font-semibold ${statusColor}`}>
            {values[accessor]}
          </span>
          
          {/* Se for GERANDO_AGENTE_CLI, exibe o Spinner rodando */}
          {isGerando && (
            <Icons.Spinner 
              width={16} 
              height={16} 
              svgStyle="animate-spin text-primario-700" 
              pathStyle="text-primario-700" 
            />
          )}
        </div>
      );
    }

    if (accessor === "permissoes" && entity === "perfil") {
      const count = values[accessor]?.length || 0;
      return <span className="font-medium block">{count}</span>;
    }

    if (accessor === "clienteFormatado") {
      return (
        <div className="flex flex-col">
          <span>{values.clienteFormatado.nome}</span>
          <span className="text-gray-500 text-sm">{values.clienteFormatado.cnpj}</span>
        </div>
      );
    }

    // Formatação original mantida apenas para a data de cadastro
    if (accessor === "dataHoraCadastro" && values[accessor]) {
      const date = new Date(values[accessor]);
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }

    // Tratamento exclusivo para a Data de Expiração com o botão de edição
    if (accessor === "dataDeExpiracao") {
      if (!values[accessor]) return "-";
      
      const date = new Date(values[accessor]);
      const formattedDate = date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      return (
        <div className="flex items-center gap-2 group">
          <span>{formattedDate}</span>
          
          {/* Só mostra o botão se for SAAS e se o usuário tiver permissão de edição */}
          {values.tipoLicenca === "SAAS" && allowedActions?.includes("editar") && (
            <button
              onClick={(e) => {
                e.stopPropagation(); // Evita cliques acidentais caso a linha seja clicável no futuro
                setCurrentExpiracaoId(values.id);
                setCurrentExpiracaoData(values[accessor]);
                setOpenExpiracao(true);
              }}
              title="Editar Data de Expiração"
              className="p-1.5 rounded-full text-primario-700 hover:bg-primario-50 transition-colors"
            >
              <Icons.Calendar pathStyle="fill-primario-700" height={16} width={16} />
            </button>
          )}
        </div>
      );
    }

    if (accessor === "identificador" && entity === "licenca") {
      const textToCopy = values[accessor];

      const fallbackCopy = (text: string) => {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      };

      const handleCopy = async () => {
        try {
          await navigator.clipboard.writeText(textToCopy);
        } catch {
          fallbackCopy(textToCopy);
        }
        toast.success("Identificador copiado com sucesso!");
      };

      return (
        <div className="flex items-center justify-center gap-2 group">
          <span>
            {textToCopy
              ? `${textToCopy.slice(0, 5)} *** ${textToCopy.slice(-5)}`
              : ""}
          </span>
          <button
            onClick={handleCopy}
            title="Copiar Identificador"
            className="p-1 rounded-md transition-opacity"
          >
            <Icons.ContentCopy
              pathStyle="fill-primario-700"
              height={15}
              width={15}
            />
          </button>
        </div>
      );
    }


    if (
      entity === "usuario" &&
      values.id === usuario.id &&
      !values.nome.includes(t("table.user.you"))
    ) {
      values.nome = `${values.nome} (${t("table.user.you")})`;
      return values[accessor];
    }

    if (accessor.includes(".")) {
      return getNestedObject(values, accessor);
    }

    return values[accessor];
  };


  useEffect(() => {
    let result = [...data];

      if (searchValue) {
        const search = searchValue.toLowerCase();
        result = result.filter((row: any) =>
          Object.entries(row).some(([key, value]) => {
          
            if (key === "clienteFormatado") {
              return row.clienteBusca.toLowerCase().includes(search);
            }
            if (value === null || value === undefined) return false;
            if (typeof value === "object") return false;
            return String(value).toLowerCase().includes(search);
          })
        );
      }

      Object.entries(columnFilters).forEach(([accessor, filterValue]) => {
        if (!filterValue) return;

        result = result.filter((row: any) => {

          if (accessor === "clienteFormatado") {
            return row.clienteBusca
              ?.toLowerCase()
              .includes(filterValue.toLowerCase());
          }

          let cellValue;

          if (accessor === "permissoes" && entity === "perfil") {
              cellValue = row[accessor]?.length || 0;
          } else {
              cellValue = accessor.includes(".")
              ? getNestedObject(row, accessor)
              : row[accessor];
          }

          return String(cellValue ?? "")
          .toLowerCase()
          .includes(filterValue.toLowerCase());
        });
    });

        // AJUSTE: Ordenação por Status (Texto) ao invés de Ativo (Boolean)
        if (entity === "usuario") {
          result.sort((a: any, b: any) => {
            // ATIVO vem primeiro que INATIVO
            if (a.status === b.status) return 0;
            return a.status === "ATIVO" ? -1 : 1;
          });
        }

      setFilteredData(result);
    }, [searchValue, columnFilters, data]);


  return (
    <div className="min-w-8xl">
      {openLog && logsRequest && (
        <LogDialog
          open={openLog}
          setOpen={setOpenLog}
          request={logsRequest}
          setRequest={setLogsRequest}
          isPolling={isPolling}
        />
      )}

      {openUpload && (
        <UploadFileDialog
          open={openUpload}
          setOpen={setOpenUpload}
          licencaId={currentUploadId}
        />
      )}

      {openFrequencia && (
        <FrequenciaDialog
          open={openFrequencia}
          setOpen={setOpenFrequencia}
          licencaId={currentFrequenciaId}
          horasAgenteSegundos={currentFrequenciaSegundos}
          onSuccess={updateTable}
        />
      )}

      {openExpiracao && (
        <ExpiracaoDialog
          open={openExpiracao}
          setOpen={setOpenExpiracao}
          licencaId={currentExpiracaoId}
          dataExpiracaoAtual={currentExpiracaoData}
          onSuccess={updateTable}
        />
      )}

      {search && (
        <div className="flex justify-between items-end gap-4 mb-8 max-md:flex-col max-md:items-stretch">
          <div className="w-full max-w-sm">
            <Input
              id="search"
              label={t("table.search")}
              onChange={handleSearchChange}
              containerClassProps="w-full"
            />
          </div>

          {buttonLabel && onButtonClick && (
            <Button
              label={buttonLabel}
              icon={<Icons.Add pathStyle="fill-inherit" />}
              color="primario"
              onClick={onButtonClick}
              classProps="justify-self-end"
              labelStyle="max-md:hidden"
              title={`Adicionar ${entity}`}
              disabled={isButtonDisabled}
            />
          )}
        </div>
      )}

      <div className="shadow-md rounded-lg h-[calc(100vh-300px)] overflow-auto">
        <ShadTable>
          {!filteredData.length && (
            <TableCaption className="m-4">
              <h1 className="text-4xl font-medium">
                {t("table.messages.notFound")}
              </h1>
            </TableCaption>
          )}

          <TableHeader className="bg-neutro-50">
            <TableRow className="border-b border-neutro-200">
               {allowedActions && (
                 <TableHead className="w-[1%] whitespace-nowrap p-4 text-center sticky top-0 z-20 bg-neutro-50">
                    {t("table.action")}
                 </TableHead>
              )}
              {columns.map((column) => (
                <TableHead key={column.accessor} className="p-1 text-primario-900 font-semibold sticky top-0 z-20 bg-neutro-50">
                  <div className="font-semibold text-sm mb-1">
                    {column.header}
                  </div>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    className="w-full px-2 py-1 text-xs rounded border border-gray-300 bg-white focus:ring-1 focus:ring-primary-500"
                    value={columnFilters[column.accessor] || ""}
                    onChange={(e) => handleColumnFilterChange(column.accessor, e.target.value)}
                  />

                </TableHead>
              ))}

              
            </TableRow>
          </TableHeader>

          <TableBody>
            {paginatedData.map((values: any, index: any) => (
              <TableRow key={index}>
                 {allowedActions && (
                  <TableCell>
                    {!(values.id === usuario.id && entity === "usuario") && (
                      <div className="">
                        <Actions id={values.id} habilitada={values.habilitada} values={values} />
                      </div> 
                    )}
                  </TableCell>
                )}
                {columns.map(({ accessor }) => (
                  <TableCell
                    key={accessor}
                    className={`whitespace-nowrap ${
                      entity === "perfil" ? "p-2" : "p-4"
                    }`}
                  >
                    <div className="ml-4">
                      {handleTableItens(accessor, values)}
                    </div>
                  </TableCell>
                ))}

                
              </TableRow>
            ))}
          </TableBody>
        </ShadTable>
      </div>
      
      <div className="flex justify-center items-center mt-4 px-2 p-2">

        <button
          disabled={page === 1 || totalPages === 0}
          onClick={() => setPage(page - 1)}
          className={`
            
            border-primario-700 text-primario-700 bg-white 
            hover:bg-primario-50 transition
            disabled:opacity-40 disabled:hover:bg-white
          `}
        >
        <Icons.ArrowLeft pathStyle="fill-primario-700"/>
        </button>

        <span className="text-neutro-700 font-medium p-2">
          {t("table.page")} {totalPages === 0 ? 0 : page} {t("table.qtdPage")} {totalPages}
        </span>

        <button
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
          className={`
            
            border-primario-700 text-primario-700 bg-white 
            hover:bg-primario-50 transition
            disabled:opacity-40 disabled:hover:bg-white
          `}
        >
        <Icons.ArrowRight pathStyle="fill-primario-700"/>
        </button>

      </div>

    </div>
  );
};

export default Table;